// Doodle generator: a seeded random scatter of hand-drawn style shapes, drawn as SVG in theme colors (so it follows the
// light/dark mode and the chosen color scheme). Same settings => same picture. Settings are stored per page/set
// (apps/portal/portfolio-doodles.js). Exposes window.Doodle = { generate, DEFAULTS, SHAPES, PRESETS, store } and Components.DoodleArt.
// The shapes react to the pointer passing near them with a random one of seven small moves (useHover below).
(function () {
  const { Box } = MaterialUI;
  const { useState, useEffect } = React;

  const W = 1000, H = 600;
  const SHAPES = ['circle', 'square', 'triangle', 'star', 'blob', 'flower', 'plus', 'squiggle', 'zigzag', 'spiral', 'arc', 'dots', 'burst',
    'diamond', 'hexagon', 'heart', 'cloud', 'moon', 'sun', 'sparkle', 'lightning', 'wave', 'stairs', 'loops', 'scribble', 'stripes', 'grid', 'rainbow',
    'pill', 'arrow', 'check', 'dashes', 'teardrop', 'donut'];
  const LINE_ONLY = new Set(['plus', 'squiggle', 'zigzag', 'spiral', 'arc', 'burst', 'sun', 'wave', 'stairs', 'loops', 'scribble', 'stripes', 'grid', 'rainbow', 'arrow', 'check', 'dashes']);
  // Ready-made shape selections for the editor.
  const PRESETS = [
    { key: 'playful', label: 'Playful', shapes: ['star', 'sparkle', 'heart', 'lightning', 'burst', 'squiggle', 'zigzag', 'arrow', 'check', 'flower'] },
    { key: 'lines', label: 'Lines & zigzags', shapes: ['zigzag', 'squiggle', 'wave', 'stairs', 'loops', 'scribble', 'stripes', 'grid', 'dashes', 'rainbow'] },
    { key: 'geometric', label: 'Geometric', shapes: ['circle', 'square', 'triangle', 'diamond', 'hexagon', 'donut', 'pill', 'plus', 'dots'] },
    { key: 'organic', label: 'Organic', shapes: ['blob', 'flower', 'cloud', 'teardrop', 'heart', 'moon', 'sun', 'spiral'] },
    { key: 'sky', label: 'Sky', shapes: ['sun', 'moon', 'cloud', 'star', 'sparkle', 'rainbow', 'lightning'] },
    { key: 'all', label: 'Everything', shapes: SHAPES.slice() }
  ];
  const DEFAULTS = { seed: 1, shapes: SHAPES.slice(), density: 45, size: 50, stroke: 3, opacity: 70, style: 'mixed', palette: 'theme' };

  // [strong, soft] color pairs; strong colors draw outlines, soft ones fill.
  const PAIRS = {
    primary: ['--m3-primary', '--m3-primary-container'],
    secondary: ['--m3-secondary', '--m3-secondary-container'],
    tertiary: ['--m3-tertiary', '--m3-tertiary-container'],
    outline: ['--m3-outline', '--m3-surface-container-highest']
  };
  const PALETTE_KEYS = { theme: ['primary', 'secondary', 'tertiary'], primary: ['primary'], secondary: ['secondary'], tertiary: ['tertiary'], mono: ['outline'] };

  function rngFor(seed) { // mulberry32
    let a = (seed >>> 0) || 1;
    return () => {
      a = (a + 0x6D2B79F5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  const f = n => Math.round(n * 10) / 10;
  const poly = pts => 'M' + pts.map(p => `${f(p[0])} ${f(p[1])}`).join('L') + 'Z';
  const polar = (r, a) => [Math.cos(a) * r, Math.sin(a) * r];
  const line = pts => 'M' + pts.map(p => `${f(p[0])} ${f(p[1])}`).join('L');

  // Each builder returns an SVG path centered on 0,0 that fits in a circle of radius r.
  const BUILD = {
    circle: r => `M${-r} 0a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0`,
    square: r => { const s = r * 0.8; return poly([[-s, -s], [s, -s], [s, s], [-s, s]]); },
    triangle: r => poly([0, 1, 2].map(i => polar(r, -Math.PI / 2 + i * 2 * Math.PI / 3))),
    star: (r, rnd) => {
      const n = 5 + Math.floor(rnd() * 3), inner = 0.4 + rnd() * 0.2;
      return poly(Array.from({ length: n * 2 }, (_, i) => polar(i % 2 ? r * inner : r, -Math.PI / 2 + i * Math.PI / n)));
    },
    blob: (r, rnd) => { // closed Catmull-Rom curve through jittered radial points
      const n = 6 + Math.floor(rnd() * 3);
      const pts = Array.from({ length: n }, (_, i) => polar(r * (0.65 + rnd() * 0.35), i * 2 * Math.PI / n));
      let d = `M${f(pts[0][0])} ${f(pts[0][1])}`;
      for (let i = 0; i < n; i++) {
        const p0 = pts[(i - 1 + n) % n], p1 = pts[i], p2 = pts[(i + 1) % n], p3 = pts[(i + 2) % n];
        d += `C${f(p1[0] + (p2[0] - p0[0]) / 6)} ${f(p1[1] + (p2[1] - p0[1]) / 6)} ${f(p2[0] - (p3[0] - p1[0]) / 6)} ${f(p2[1] - (p3[1] - p1[1]) / 6)} ${f(p2[0])} ${f(p2[1])}`;
      }
      return d + 'Z';
    },
    flower: (r, rnd) => {
      const n = 5 + Math.floor(rnd() * 3), pr = r * 0.36;
      let d = '';
      for (let i = 0; i < n; i++) {
        const [x, y] = polar(r - pr, i * 2 * Math.PI / n);
        d += `M${f(x - pr)} ${f(y)}a${f(pr)} ${f(pr)} 0 1 0 ${f(2 * pr)} 0a${f(pr)} ${f(pr)} 0 1 0 ${f(-2 * pr)} 0`;
      }
      return d;
    },
    plus: r => `M0 ${-r}V${r}M${-r} 0H${r}`,
    squiggle: (r, rnd) => {
      const waves = 2 + Math.floor(rnd() * 2), amp = r * 0.3;
      return line(Array.from({ length: 41 }, (_, i) => { const t = i / 40; return [-r + t * 2 * r, Math.sin(t * waves * 2 * Math.PI) * amp]; }));
    },
    zigzag: (r, rnd) => {
      const n = 4 + Math.floor(rnd() * 3), amp = r * 0.35;
      return line(Array.from({ length: n + 1 }, (_, i) => [-r + i * 2 * r / n, i % 2 ? amp : -amp]));
    },
    spiral: (r, rnd) => {
      const turns = 2 + rnd() * 1.2;
      return line(Array.from({ length: 70 }, (_, i) => { const t = i / 69; return polar(r * t, t * turns * 2 * Math.PI); }));
    },
    arc: r => `M${-r} ${r * 0.4}A${r} ${r} 0 0 1 ${r} ${r * 0.4}`,
    dots: r => {
      const s = r * 0.62, dr = r * 0.14;
      let d = '';
      for (const x of [-s, 0, s]) for (const y of [-s, 0, s]) d += `M${f(x - dr)} ${f(y)}a${f(dr)} ${f(dr)} 0 1 0 ${f(2 * dr)} 0a${f(dr)} ${f(dr)} 0 1 0 ${f(-2 * dr)} 0`;
      return d;
    },
    diamond: r => poly([[0, -r], [r * 0.7, 0], [0, r], [-r * 0.7, 0]]),
    hexagon: r => poly(Array.from({ length: 6 }, (_, i) => polar(r, i * Math.PI / 3))),
    heart: r => `M0 ${f(r * 0.9)}C${f(-r * 1.3)} ${f(r * 0.1)} ${f(-r * 0.8)} ${f(-r * 0.95)} 0 ${f(-r * 0.35)}C${f(r * 0.8)} ${f(-r * 0.95)} ${f(r * 1.3)} ${f(r * 0.1)} 0 ${f(r * 0.9)}Z`,
    cloud: r => `M${f(-r * 0.7)} ${f(r * 0.4)}A${f(r * 0.3)} ${f(r * 0.3)} 0 0 1 ${f(-r * 0.5)} ${f(-r * 0.15)}A${f(r * 0.4)} ${f(r * 0.4)} 0 0 1 ${f(r * 0.25)} ${f(-r * 0.3)}A${f(r * 0.35)} ${f(r * 0.35)} 0 0 1 ${f(r * 0.7)} ${f(r * 0.4)}Z`,
    moon: r => `M${f(r * 0.3)} ${-r}A${r} ${r} 0 1 0 ${f(r * 0.3)} ${r}A${f(r * 1.25)} ${f(r * 1.25)} 0 0 1 ${f(r * 0.3)} ${-r}Z`,
    sun: r => {
      const c = r * 0.4;
      let d = `M${-c} 0a${f(c)} ${f(c)} 0 1 0 ${f(2 * c)} 0a${f(c)} ${f(c)} 0 1 0 ${f(-2 * c)} 0`;
      for (let i = 0; i < 8; i++) { const a = i * Math.PI / 4, [x1, y1] = polar(r * 0.62, a), [x2, y2] = polar(r, a); d += `M${f(x1)} ${f(y1)}L${f(x2)} ${f(y2)}`; }
      return d;
    },
    sparkle: r => { const k = r * 0.12; return `M0 ${-r}Q${f(k)} ${f(-k)} ${r} 0Q${f(k)} ${f(k)} 0 ${r}Q${f(-k)} ${f(k)} ${-r} 0Q${f(-k)} ${f(-k)} 0 ${-r}Z`; },
    lightning: r => poly([[0.2, -1], [-0.5, 0.1], [-0.05, 0.1], [-0.25, 1], [0.5, -0.2], [0.05, -0.2]].map(([x, y]) => [x * r, y * r])),
    wave: (r, rnd) => {
      const waves = 1.5 + Math.floor(rnd() * 2), amp = r * 0.2;
      return [-0.5, 0, 0.5].map(o => line(Array.from({ length: 31 }, (_, i) => { const t = i / 30; return [-r + t * 2 * r, o * r + Math.sin(t * waves * 2 * Math.PI) * amp]; }))).join('');
    },
    stairs: r => { const n = 4, w = 2 * r / n; const pts = [[-r, r]]; for (let i = 0; i < n; i++) { pts.push([-r + i * w, r - (i + 1) * w * 0.9], [-r + (i + 1) * w, r - (i + 1) * w * 0.9]); } return line(pts); },
    loops: r => line(Array.from({ length: 80 }, (_, i) => { const t = i / 79; return [-r + 2 * r * t + r * 0.28 * Math.sin(t * 8 * Math.PI) * 0.9, r * 0.3 * Math.cos(t * 8 * Math.PI)]; })),
    scribble: (r, rnd) => line(Array.from({ length: 9 }, () => polar(r * Math.sqrt(rnd()), rnd() * 2 * Math.PI))),
    stripes: r => [-0.6, -0.2, 0.2, 0.6].map(o => `M${-r} ${f(o * r)}H${r}`).join(''),
    grid: r => `M${f(-r * 0.35)} ${-r}V${r}M${f(r * 0.35)} ${-r}V${r}M${-r} ${f(-r * 0.35)}H${r}M${-r} ${f(r * 0.35)}H${r}`,
    rainbow: r => [1, 0.7, 0.4].map(k => `M${f(-r * k)} ${f(r * 0.5)}A${f(r * k)} ${f(r * k)} 0 0 1 ${f(r * k)} ${f(r * 0.5)}`).join(''),
    pill: r => { const h = r * 0.35, w = r * 0.65; return `M${f(-w)} ${f(-h)}H${f(w)}a${f(h)} ${f(h)} 0 0 1 0 ${f(2 * h)}H${f(-w)}a${f(h)} ${f(h)} 0 0 1 0 ${f(-2 * h)}Z`; },
    arrow: r => `M${-r} 0H${r}M${f(r * 0.45)} ${f(-r * 0.45)}L${r} 0L${f(r * 0.45)} ${f(r * 0.45)}`,
    check: r => line([[-r * 0.8, 0], [-r * 0.25, r * 0.55], [r * 0.85, -r * 0.6]]),
    dashes: r => Array.from({ length: 4 }, (_, i) => `M${f(-r + i * r * 0.55)} 0h${f(r * 0.32)}`).join(''),
    teardrop: r => `M0 ${-r}C${f(r * 0.2)} ${f(-r * 0.5)} ${f(r * 0.7)} ${f(-r * 0.1)} ${f(r * 0.7)} ${f(r * 0.3)}a${f(r * 0.7)} ${f(r * 0.7)} 0 0 1 ${f(-r * 1.4)} 0C${f(-r * 0.7)} ${f(-r * 0.1)} ${f(-r * 0.2)} ${f(-r * 0.5)} 0 ${-r}Z`,
    donut: r => { const q = r * 0.45; return `M${-r} 0a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0M${-q} 0a${f(q)} ${f(q)} 0 1 1 ${f(2 * q)} 0a${f(q)} ${f(q)} 0 1 1 ${f(-2 * q)} 0Z`; },
    burst: (r, rnd) => {
      const n = 6 + Math.floor(rnd() * 5);
      return Array.from({ length: n }, (_, i) => { const a = i * 2 * Math.PI / n; const [x1, y1] = polar(r * 0.35, a), [x2, y2] = polar(r, a); return `M${f(x1)} ${f(y1)}L${f(x2)} ${f(y2)}`; }).join('');
    }
  };

  // Returns the list of drawn shapes: [{ type, d, x, y, rot, stroke, fill, filled }]. Placement avoids overlaps (best effort).
  function generate(cfg) {
    const c = { ...DEFAULTS, ...cfg };
    const rnd = rngFor(c.seed);
    const types = (c.shapes && c.shapes.length ? c.shapes : SHAPES).filter(s => BUILD[s]);
    const keys = PALETTE_KEYS[c.palette] || PALETTE_KEYS.theme;
    const target = Math.round(6 + (c.density / 100) * 44);
    const baseR = 22 + (c.size / 100) * 78;
    const placed = [], out = [];
    for (let i = 0, tries = 0; out.length < target && tries < target * 30; tries++) {
      const r = baseR * (0.55 + rnd() * 0.75);
      const x = rnd() * W, y = rnd() * H, type = types[Math.floor(rnd() * types.length)];
      const rotation = rnd() * 360, pick = Math.floor(rnd() * keys.length), fillRoll = rnd();
      const shapeSeed = Math.floor(rnd() * 1e9);
      if (placed.some(p => Math.hypot(p.x - x, p.y - y) < (p.r + r) * 0.82)) continue;
      placed.push({ x, y, r });
      const pair = PAIRS[keys[pick]];
      const filled = !LINE_ONLY.has(type) && (c.style === 'fill' || (c.style === 'mixed' && fillRoll < 0.5));
      out.push({
        id: i++, type, x: f(x), y: f(y), rot: f(rotation), d: BUILD[type](r, rngFor(shapeSeed)),
        stroke: `var(${pair[0]})`, fill: filled ? `var(${pair[1]})` : 'none', filled
      });
    }
    return out;
  }

  // ---- shared store: one request for every doodle on the portfolio ------------------------------------------------
  const listeners = new Set();
  const store = {
    items: {}, canEdit: false, loaded: false, loading: null,
    token() { try { return localStorage.getItem('wb_token') || ''; } catch (e) { return ''; } },
    async req(method, url, body) {
      const headers = {};
      if (this.token()) headers.Authorization = `Bearer ${this.token()}`;
      if (body) headers['Content-Type'] = 'application/json';
      const res = await fetch(url, { method, headers, body: body ? JSON.stringify(body) : undefined });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      return data;
    },
    load() {
      if (!this.loading) {
        this.loading = this.req('GET', '/api/portfolio/doodles')
          .then(d => { this.items = d.items || {}; this.canEdit = !!d.canEdit; })
          .catch(() => {})
          .then(() => { this.loaded = true; listeners.forEach(fn => fn()); });
      }
      return this.loading;
    },
    async save(target, cfg) {
      const saved = await this.req('PUT', `/api/portfolio/doodles/${encodeURIComponent(target)}`, cfg);
      this.items = { ...this.items, [target]: saved };
      listeners.forEach(fn => fn());
      return saved;
    },
    async remove(target) {
      await this.req('DELETE', `/api/portfolio/doodles/${encodeURIComponent(target)}`);
      const next = { ...this.items }; delete next[target];
      this.items = next;
      listeners.forEach(fn => fn());
    }
  };

  // Subscribes a component to the store. Returns { doodle, canEdit, loaded } for one target.
  function useDoodle(target) {
    const [, tick] = useState(0);
    const editOn = window.EditMode.use();
    useEffect(() => {
      const fn = () => tick(n => n + 1);
      listeners.add(fn);
      store.load();
      return () => { listeners.delete(fn); };
    }, []);
    return { doodle: store.items[target] || null, canEdit: store.canEdit && editOn, loaded: store.loaded };
  }

  // The picture itself. Fills its (positioned) parent; decorative, so hidden from assistive tech.
  // Hover life: the doodles sit behind the page with pointer events off, so the pointer is followed across the whole window. When it comes near a
  // shape the shape gets a kick and springs back by itself. Each kick picks one of seven moves at random (never the same twice in a row):
  //   wiggle  a quick turn, a pop in size and a nudge away      spin   one full turn          hop    a jump and a landing bounce
  //   jelly   squash and stretch that wobbles out              shy    darts away, drifts back slowly
  //   swing   a slow pendulum swing                             pulse  a double heartbeat
  // Nothing runs while nothing moves; touch and "reduce motion" are left out.
  const R2 = n => n * (Math.random() < 0.5 ? -1 : 1);
  const MOVES = {
    wiggle: (o, away) => { Object.assign(o, { K: 170, C: 9.5 }); o.va += R2(240 + Math.random() * 160); o.vk += 2.2 + Math.random() * 0.8; o.vx += away[0] * 240; o.vy += away[1] * 240; },
    spin: o => { Object.assign(o, { K: 42, C: 11 }); o.ta = R2(360); o.vk += 1.4; },
    hop: o => { Object.assign(o, { K: 230, C: 8 }); o.vy -= 300 + Math.random() * 90; o.vk += 0.9; o.va += R2(60); },
    jelly: o => { Object.assign(o, { K: 270, C: 6.5 }); const f = 3 + Math.random(); o.vsx += f; o.vsy -= f; o.va += R2(40); },
    shy: (o, away) => { Object.assign(o, { K: 20, C: 5.2 }); o.vx += away[0] * 560; o.vy += away[1] * 560; o.vk -= 0.9; o.va += R2(120); },
    swing: o => { Object.assign(o, { K: 60, C: 3.1 }); o.va += R2(560 + Math.random() * 160); },
    pulse: o => { Object.assign(o, { K: 420, C: 11.5 }); o.vk += 4.4; }
  };
  const MOVE_NAMES = Object.keys(MOVES);

  function useHover(svgRef, nodes, shapes, size) {
    useEffect(() => {
      const svg = svgRef.current;
      if (!svg) return undefined;
      const R = 30 + size * 0.7; // reach, in picture units
      const blank = () => ({ a: 0, va: 0, ta: 0, k: 0, vk: 0, sx: 0, vsx: 0, sy: 0, vsy: 0, ox: 0, oy: 0, vx: 0, vy: 0, K: 170, C: 9.5, inside: false, live: false });
      const st = new Map(shapes.map(sh => [sh.id, blank()]));
      let raf = 0, last = 0, lastMove = '';
      const paint = (sh, o) => { const n = nodes.current[sh.id]; if (n) n.setAttribute('transform', `translate(${sh.x + o.ox} ${sh.y + o.oy}) rotate(${sh.rot + o.a}) scale(${1 + o.k + o.sx} ${1 + o.k + o.sy})`); };
      const tick = t => {
        const dt = Math.min(0.033, (t - (last || t)) / 1000); last = t;
        let busy = false;
        for (const sh of shapes) {
          const o = st.get(sh.id);
          if (!o.live) continue;
          for (const [x, v, to] of [['a', 'va', o.ta], ['k', 'vk', 0], ['sx', 'vsx', 0], ['sy', 'vsy', 0], ['ox', 'vx', 0], ['oy', 'vy', 0]]) {
            o[v] += (-o.K * (o[x] - to) - o.C * o[v]) * dt; o[x] += o[v] * dt;
          }
          const rest = Math.abs(o.a - o.ta) + Math.abs(o.va) * 0.5 + (Math.abs(o.k) + Math.abs(o.sx) + Math.abs(o.sy)) * 100 + (Math.abs(o.vk) + Math.abs(o.vsx) + Math.abs(o.vsy)) * 10
            + Math.abs(o.ox) + Math.abs(o.oy) + (Math.abs(o.vx) + Math.abs(o.vy)) * 0.1;
          if (rest < 0.35) { Object.assign(o, blank(), { inside: o.inside }); } else busy = true; // a full turn ends where it began
          paint(sh, o);
        }
        raf = busy ? requestAnimationFrame(tick) : 0;
        if (!busy) last = 0;
      };
      const onMove = e => {
        if (e.pointerType === 'touch' || (window.M3Core && window.M3Core.reducedMotion())) return; // also follows the Accessibility switch live
        const r = svg.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) { st.forEach(o => { o.inside = false; }); return; } // left the picture: every shape can react again
        const sc = Math.max(r.width / W, r.height / H); // the picture is scaled to cover its box, centred
        const px = (e.clientX - r.left - (r.width - W * sc) / 2) / sc, py = (e.clientY - r.top - (r.height - H * sc) / 2) / sc;
        let kicked = false;
        for (const sh of shapes) {
          const o = st.get(sh.id);
          const dx = sh.x - px, dy = sh.y - py, dist = Math.hypot(dx, dy);
          if (dist < R) {
            if (!o.inside) {
              o.inside = true; o.live = true; kicked = true;
              const away = dist > 1 ? [dx / dist, dy / dist] : [Math.random() - 0.5, Math.random() - 0.5];
              const pool = MOVE_NAMES.filter(m => m !== lastMove);
              lastMove = pool[Math.floor(Math.random() * pool.length)];
              MOVES[lastMove](o, away);
              svg.setAttribute('data-last-move', lastMove); // handy for tests
            }
          } else if (dist > R * 1.35) o.inside = false;
        }
        if (kicked && !raf) raf = requestAnimationFrame(tick);
      };
      window.addEventListener('pointermove', onMove, { passive: true });
      return () => {
        window.removeEventListener('pointermove', onMove);
        if (raf) cancelAnimationFrame(raf);
        for (const sh of shapes) paint(sh, blank());
      };
    }, [shapes, size]);
  }

  function DoodleArt({ config, fade = false, sx }) {
    const cfg = { ...DEFAULTS, ...config };
    const shapes = React.useMemo(() => generate(cfg), [cfg.seed, cfg.density, cfg.size, cfg.style, cfg.palette, cfg.shapes.join()]);
    const svgRef = React.useRef(null);
    const nodes = React.useRef({});
    useHover(svgRef, nodes, shapes, cfg.size);
    return (
      <Box
        aria-hidden="true"
        className="wb-doodle"
        sx={[{
          position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden',
          ...(fade ? { maskImage: 'linear-gradient(to bottom, #000 35%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to bottom, #000 35%, transparent 100%)' } : {})
        }, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" width="100%" height="100%" style={{ display: 'block', opacity: cfg.opacity / 100 }}>
          <g strokeWidth={cfg.stroke * 1.5} strokeLinecap="round" strokeLinejoin="round">
            {shapes.map(s => (
              <path key={s.id} ref={n => { nodes.current[s.id] = n; }} d={s.d} transform={`translate(${s.x} ${s.y}) rotate(${s.rot})`} stroke={s.filled ? 'none' : s.stroke} fill={s.fill} />
            ))}
          </g>
        </svg>
      </Box>
    );
  }

  window.Doodle = { generate, DEFAULTS, SHAPES, PRESETS, store, useDoodle };
  window.Components = window.Components || {};
  window.Components.DoodleArt = DoodleArt;
})();
